/**
 * PowerSync Provider para SolidJS — centraliza la inicialización de la base de datos
 * y la orquestación de conectividad compartida.
 */

import type { AbstractPowerSyncDatabase } from "@powersync/common";
import {
  connectivityMachine,
  createPowerSyncDatabase,
  handlePowerSyncAutoConnect,
  MealplanPowerSyncConnector,
  resolvePowerSyncUrl,
  startConnectivityManager,
  VISITOR_ID,
} from "@mealplan/shared";
import { type ActorRefFrom, createActor } from "xstate";
import { fromActorRef } from "@xstate/solid";
import { sessionActor } from "@/lib/session-machine";
import { POWERSYNC_URL, SERVER_URL } from "@/lib/runtime-config";
import {
  type Accessor,
  batch,
  createContext,
  createEffect,
  createSignal,
  onCleanup,
  type ParentComponent,
  untrack,
  useContext,
} from "solid-js";
import { api } from "@/lib/api";
import {
  readAccountsStateSync,
  readActiveAccountSelector,
  readActiveAccountSync,
} from "@/lib/accounts";
import { parseActiveIndexFromPath, resolvePerTabSyncUserId } from "@/lib/active-account";
import { activePathname } from "@/lib/reactive-location";
import { installTabAccountPin } from "@/lib/tab-account-pin";
import { selectDatabaseFactory } from "@/lib/powersync/select-factory";
import { handleTauriPowerSyncAutoConnect, tauriDisconnect } from "@/lib/powersync/tauri-connect";
import { tokenStorage } from "@/lib/powersync/token-storage";
import { isTauriRuntime } from "@/lib/platform";
import { clearAllScheduledReminders } from "@/lib/notifications/service";

// ─── Conector PowerSync (web) ─────────────────────────
// Multi-account: each account caches its PowerSync JWT under its own namespace
// (the active user's id) so two accounts never clobber each other's cached
// credentials. The connector is rebuilt when the active user changes; the
// `fetchAuthToken` path uses the shared `api` client, which already attaches
// the ACTIVE account's Bearer token, so the fetched JWT belongs to whoever is
// active. A `null` namespace (no active user) keeps the legacy keys.
const POWERSYNC_ENDPOINT = resolvePowerSyncUrl({
  isWeb: true,
  isDev: import.meta.env.DEV,
  serverUrl: SERVER_URL,
  powerSyncUrl: POWERSYNC_URL,
  location: {
    hostname: globalThis.window.location.hostname,
    protocol: globalThis.window.location.protocol,
    origin: globalThis.window.location.origin,
  },
});

function createWebConnector(userId: string | null): MealplanPowerSyncConnector {
  const ownKey = userId && userId !== VISITOR_ID ? userId : VISITOR_ID;
  return new MealplanPowerSyncConnector({
    endpoint: POWERSYNC_ENDPOINT,
    storage: tokenStorage,
    apiClient: api,
    cacheNamespace: userId && userId !== VISITOR_ID ? userId : undefined,
    // CC-42 upload gate: `uploadBatch` posts through the SHARED `api` client,
    // whose per-request identity is the tab's ACTIVE account (`X-Active-Account`
    // selector on web). A non-active account's DB can still flush its queued
    // CRUD (initial-sync upload throttle + account switch), which the server
    // would authenticate as the OTHER account and silently drop (`user_op`) or
    // misattribute — permanent data loss. Defer instead: ops stay queued and
    // are re-triggered when this account becomes active again (see
    // `loadDbForUser`). On Tauri the sync loop lives in Rust (this JS uploader
    // is not the upload path), so the gate stays open there.
    canUploadNow: () => isTauriRuntime() || (readActiveAccountSelector() ?? VISITOR_ID) === ownKey,
    // PER-CONNECTOR (not global): a connector fetches a token ONLY for its OWN
    // real account. The guest connector (`userId === null`) NEVER fetches/syncs —
    // even while a real account stays globally signed-in in another tab — so a
    // guest tab is strictly local-only and its local rows never get uploaded/
    // cleared. The `api` client attaches the active tab account's Bearer, so the
    // fetched JWT belongs to this connector's account.
    shouldFetchToken: () => userId !== null && userId !== VISITOR_ID,
    onAuthError: (error) => {
      console.warn("[PowerSync] Auth fallida (401/403). Notificando a la máquina.");
      sessionActor.send({
        type: "SERVER_REPLY",
        session: null,
        error,
      });
    },
  });
}

interface PowerSyncContextValue {
  db: Accessor<AbstractPowerSyncDatabase | null>;
  isLoading: Accessor<boolean>;
  error: Accessor<Error | null>;
  disconnectAndClear: () => Promise<void>;
  /**
   * Destroys + forgets a specific account's local database (used by
   * remove-account in the switcher). Unlike `disconnectAndClear`, the account
   * need not be the active one.
   */
  destroyAccountData: (userId: string) => Promise<void>;
  /**
   * Flushes the guest DB's freshly-merged rows to the server as `userId` so the
   * real account's per-user DB can sync them (guest→real upgrade). No-op if
   * there is no guest DB instance.
   */
  flushGuestUpgrade: (userId: string) => Promise<void>;
  connectivityRef: ActorRefFrom<typeof connectivityMachine>;
}

const PowerSyncContext = createContext<PowerSyncContextValue>();

// Multi-account: one PowerSync database instance PER user id (each backed by
// its own on-disk DB file via `dbFilenameForUser`). Switching accounts swaps
// the active instance WITHOUT clearing any data — only a real sign-out /
// remove-account destroys an instance. The guest/visitor uses VISITOR_ID.
interface DbInstance {
  db: AbstractPowerSyncDatabase;
  connector: MealplanPowerSyncConnector;
}

const dbInstances = new Map<string, DbInstance>();
const dbInstancePromises = new Map<string, Promise<DbInstance>>();

function userKey(userId: string | null | undefined): string {
  return userId && userId !== VISITOR_ID ? userId : VISITOR_ID;
}

async function getOrCreateDbForUser(userId: string | null): Promise<DbInstance> {
  const key = userKey(userId);
  const existing = dbInstances.get(key);
  if (existing) {
    return existing;
  }

  let pending = dbInstancePromises.get(key);
  if (!pending) {
    pending = (async () => {
      const isDev = import.meta.env.DEV;
      const factory = await selectDatabaseFactory();
      const database = await createPowerSyncDatabase(factory, {
        isDev,
        userId: key === VISITOR_ID ? null : key,
      });
      await database.init();
      const instance: DbInstance = {
        db: database,
        connector: createWebConnector(key === VISITOR_ID ? null : key),
      };
      dbInstances.set(key, instance);
      return instance;
    })();
    // CRITICAL: the in-flight promise must NOT outlive its own settlement in the
    // cache. We keep it ONLY while pending (to dedupe concurrent callers); the
    // resolved instance lives in `dbInstances` (the fast path above), and on
    // FAILURE the slot must be released so a later effect re-run can retry.
    // Otherwise a transient `init()` failure (e.g. the SHARED sync worker being
    // torn down by a sibling DB's `disconnectAndClear()` during a real-account
    // sign-out, which races a concurrent guest-DB (re)build) would cache a
    // REJECTED promise forever: every subsequent `getOrCreateDbForUser(null)`
    // re-returns the same rejection, the guest DB never settles, and the app
    // hangs on the boot shell after switching back to the guest. Clearing the
    // pending entry on settle (both paths) makes the rebuild self-healing.
    const tracked = pending;
    void tracked.finally(() => {
      if (dbInstancePromises.get(key) === tracked) {
        dbInstancePromises.delete(key);
      }
    });
    dbInstancePromises.set(key, pending);
  }

  return pending;
}

/** Destroys + forgets a user's DB instance (real sign-out / remove account). */
async function destroyDbForUser(userId: string | null): Promise<void> {
  const key = userKey(userId);
  const instance = dbInstances.get(key);
  dbInstances.delete(key);
  dbInstancePromises.delete(key);
  if (!instance) {
    return;
  }
  try {
    await instance.connector.clearTokenCache();
    await instance.db.disconnectAndClear();
  } catch (err) {
    console.warn("[PowerSyncProvider] destroyDbForUser failed:", err);
  }
}

/**
 * Guest → real upgrade (merge): after `sweepVisitorData` re-stamps the guest's
 * rows + queued CRUD from `VISITOR_ID` to the real user in the guest DB
 * (`mealplan.db`), those writes are stranded — the app is about to swap the active
 * DB to the real account's own file (`mealplan-<userId>.db`), and the guest DB is
 * never the active/connected DB so it would never upload.
 *
 * This flushes them via the CLOUD (the source of truth per user): connect the
 * guest DB with a connector authenticated as the real account, wait for its
 * upload queue to drain to the server, then disconnect. The real account's DB
 * then downloads the merged rows on its normal initial sync — and they survive a
 * full reload, because the data now lives server-side, not just in `mealplan.db`.
 *
 * GUEST-DATA LIFECYCLE (no duplication): once the upload has drained, the data
 * lives in the real account (cloud + its own per-user DB). The guest scratch
 * (`mealplan.db`) must therefore be CLEARED — otherwise the same rows linger in
 * the guest entry and a later "Continue as guest" would still show them, which
 * is the user-reported duplication bug. We `disconnectAndClear()` the guest DB
 * and forget its instance so the guest entry stays available as a FRESH EMPTY
 * scratch. This runs on BOTH the register auto-claim and the login merge paths
 * (both call `flushGuestUpgrade`).
 */
async function flushGuestDataToServer(realUserId: string): Promise<void> {
  const guest = dbInstances.get(VISITOR_ID);
  if (!guest) {
    return;
  }
  // A connector namespaced to the real user so the fetched PowerSync JWT (whose
  // `sub` is the real user, via the now-active real Bearer) is cached correctly.
  // NOTE: we must NOT reuse the standard connector here — its `shouldFetchToken`
  // gates on the session machine, which has NOT yet been primed as the real user
  // at this point in the upgrade, so it would refuse to fetch a token and the
  // connect would silently fail (connected=false, nothing uploads). This
  // dedicated connector always fetches: we KNOW the real account's Bearer is the
  // active one (it was just registered) and that we are authenticated.
  const connector = new MealplanPowerSyncConnector({
    endpoint: POWERSYNC_ENDPOINT,
    storage: tokenStorage,
    apiClient: api,
    cacheNamespace: realUserId,
  });
  try {
    await guest.db.connect(connector);
    // Wait until the local CRUD queue has been uploaded (or a short timeout).
    // We poll a non-consuming COUNT of the `ps_crud` queue rather than
    // `getNextCrudTransaction()` (which the connector's own uploader also calls)
    // so we never race/steal a transaction from the uploader.
    const DEADLINE_MS = 15_000;
    const POLL_MS = 250;
    const start = Date.now();
    while (Date.now() - start < DEADLINE_MS) {
      // oxlint-disable-next-line no-await-in-loop
      // eslint-disable-next-line lingui/no-unlocalized-strings -- SQL, not UI copy
      const rows = await guest.db.getAll<{ n: number }>("SELECT count(*) AS n FROM ps_crud");
      if ((rows[0]?.n ?? 0) === 0) {
        break;
      }
      // Not yet drained — give the connector's uploader a moment to flush.
      // oxlint-disable-next-line no-await-in-loop
      await new Promise<void>((resolve) => {
        setTimeout(resolve, POLL_MS);
      });
    }
  } catch (err) {
    console.warn("[PowerSyncProvider] flushGuestDataToServer failed:", err);
  } finally {
    // Clear the guest scratch so the migrated rows live ONLY in the real
    // account (no duplication). `disconnectAndClear` both stops the sync loop
    // and wipes the local DB + its CRUD queue; we then forget the instance so
    // the next "Continue as guest" rebuilds a fresh empty `mealplan.db`.
    try {
      await guest.db.disconnectAndClear();
    } catch (clearErr) {
      console.warn("[PowerSyncProvider] failed to clear guest scratch:", clearErr);
      // Best-effort fallback: at least stop the sync loop.
      try {
        await guest.db.disconnect();
      } catch {
        // ignore
      }
    }
    dbInstances.delete(VISITOR_ID);
    dbInstancePromises.delete(VISITOR_ID);
  }
}

export const PowerSyncProvider: ParentComponent = (props) => {
  const [db, setDb] = createSignal<AbstractPowerSyncDatabase | null>(null);
  const [isLoading, setIsLoading] = createSignal<boolean>(true);
  const [syncError, setSyncError] = createSignal<Error | null>(null);
  // The connector bound to the currently-active DB instance. Swapped on account
  // switch so the auto-connect effect always uses the active account's connector.
  const [activeConnector, setActiveConnector] = createSignal<MealplanPowerSyncConnector | null>(
    null,
  );

  // 1. Señales reactivas del actor de sesión
  const snapshot = fromActorRef(sessionActor);
  const currentSession = () => snapshot().context.session;
  const isVisitor = () => snapshot().context.isVisitor;
  const isPriming = () => snapshot().matches("priming");

  // 1b. REACTIVE per-tab active account ← router `/u/<n>`.
  //
  // The per-tab active account is encoded in the URL path prefix `/u/<n>` (see
  // `lib/active-account.ts`). An IN-APP account switch (the switcher) navigates
  // to a new `/u/<m>` WITHOUT a reload — the URL changes but no session signal
  // necessarily changes the way the DB-binding effect tracks (and a synchronous
  // `globalThis.location.pathname` read is NOT reactive), so the effect that
  // binds the per-user PowerSync DB would not re-run and the displayed data
  // would not swap until a hard refresh. To make the switch reactive we mirror
  // the router's current pathname into a Solid signal here.
  //
  // The provider is mounted ABOVE `<RouterProvider>`, so the `useLocation()`
  // hook (which needs the router *context*) is unavailable, and importing the
  // `@/router` singleton to subscribe to its lifecycle creates a massive import
  // cycle (router → routes → components/providers → router). Instead we track
  // the browser History API directly (router-agnostic) — see
  // `lib/reactive-location.ts`. This catches every navigation (SPA push/replace
  // + back/forward), so the DB-binding effect re-runs on an in-app switch.
  const routerPathname = activePathname;

  // Keep THIS tab pinned to the account it is showing (by userId) across
  // registry mutations made in OTHER tabs. Without this, signing out / removing
  // an account in one tab shifts the positional `/u/<n>` indices, so other open
  // tabs would silently point at a DIFFERENT account (the per-tab data-leak the
  // multi-tab matrix exercises). Lifetime singleton; idempotent.
  installTabAccountPin();

  // 2. Inicializar la máquina de conectividad y el orquestador compartido
  const connectivityRef = createActor(connectivityMachine).start();
  const manager = startConnectivityManager({
    getDb: db,
    apiClient: api,
    connectivityRef,
    // NOTE: intentionally no `onInvalidateQueries`. Real-time freshness is owned
    // entirely by each query's PowerSync subscription (`onChangeWithCallback` +
    // `triggerImmediate` in `createPowerSyncQuery`), which refetches only the
    // queries whose own tables changed — granular and identical on every
    // platform (the @powersync/tauri-plugin forwards table-change events to JS).
    // A blanket `invalidateQueries()` here refetched *everything* on every sync
    // settle, which is what flashed the theme/background after a mutation and is
    // needlessly heavy on mobile. Screens that need a guaranteed-fresh pull on
    // entry should invalidate their own keys via TanStack at that point.
  });

  onCleanup(() => {
    manager.dispose();
  });

  // API Health Check periódico (gestionado por el manager)
  createEffect(() => {
    if (navigator.onLine) {
      void manager.startHealthCheck();
    } else {
      manager.stopHealthCheck();
    }
  });

  // Foreground re-probe (bug PWA móvil): al volver la pestaña a primer plano —
  // cambio de app (`visibilitychange`), restauración desde el bfcache de iOS
  // Safari (`pageshow`) o foco de ventana (`focus`) — el navegador móvil ha
  // suspendido/estrangulado el intervalo de health-check y cualquier ping en
  // vuelo pudo fallar, dejando `apiStatus="down"`. Como la red no cambió de
  // verdad NO se dispara ningún evento `online`, así que nada forzaba un
  // re-chequeo y el banner "El servidor no responde" quedaba pegado hasta un
  // refresco manual. Aquí re-sincronizamos el estado de red y forzamos un
  // sondeo inmediato de la API para que la máquina de conectividad vuelva a
  // "up" SIN recargar la página.
  createEffect(() => {
    const handleForeground = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      manager.handlers.onNetworkChanged(navigator.onLine);
      void manager.forceHealthCheck();
    };

    document.addEventListener("visibilitychange", handleForeground);
    globalThis.addEventListener("pageshow", handleForeground);
    globalThis.addEventListener("focus", handleForeground);

    onCleanup(() => {
      document.removeEventListener("visibilitychange", handleForeground);
      globalThis.removeEventListener("pageshow", handleForeground);
      globalThis.removeEventListener("focus", handleForeground);
    });
  });

  // Listener para el estado de red del navegador
  createEffect(() => {
    const handleOnline = () => {
      manager.handlers.onNetworkChanged(true);
    };
    const handleOffline = () => {
      manager.handlers.onNetworkChanged(false);
    };

    globalThis.addEventListener("online", handleOnline);
    globalThis.addEventListener("offline", handleOffline);

    // Estado inicial
    manager.handlers.onNetworkChanged(navigator.onLine);

    onCleanup(() => {
      globalThis.removeEventListener("online", handleOnline);
      globalThis.removeEventListener("offline", handleOffline);
    });
  });

  // Listener para el estado de autenticación.
  //
  // PER-TAB coherence: the connectivity machine (and therefore the sync pill)
  // must reflect THIS tab's ACTIVE account — coherent with
  // `useSession().isAuthenticated()` — NOT only the GLOBAL session machine.
  // Gating on `currentSession()?.user` meant that when the active per-tab
  // account is a REAL account but the global machine is not (yet) primed for it
  // (the minimal-view window right after a login / account switch, or a real
  // account open only in another tab), `onAuthChanged(false)` drove the machine
  // to `unauthenticated` → the pill showed "Visitor"/"Unauthenticated" and sync
  // never connected, even though the Profile correctly showed the user logged in.
  // `activeUserId()` resolves the per-tab account (URL `/u/<n>` → registry), so a
  // real active account reports authenticated regardless of global priming.
  createEffect(() => {
    manager.handlers.onAuthChanged(activeAuthenticated());
  });

  // Monitorizar el estado real de PowerSync (gestionado por el manager)
  createEffect(() => {
    const database = db();
    if (database) {
      manager.setupDbListener(database);
    }
  });

  // Auto-conexión a PowerSync (gestionada por lógica compartida)
  // Las señales de sesión (currentSession, isVisitor, isPriming) se leen de forma
  // reactiva para que el effect se re-ejecute cuando la sesión cambie (ej. login).
  // isConnecting se lee con untrack() para evitar que los fallos de conexión
  // (ej. 429 rate-limit) re-disparen el effect en un bucle infinito.
  const [isConnecting, setIsConnecting] = createSignal(false);
  const onTauri = isTauriRuntime();
  createEffect(() => {
    const database = db();
    if (!database) {
      return;
    }

    // PER-TAB override (boot-safe layered, mirrors useSession): a GUEST active
    // tab is strictly local-only — force visitor=true / authenticated=false so
    // the effect NEVER connects the guest DB, even while a real account stays
    // globally signed-in in another tab (otherwise the guest's local rows would
    // be uploaded/cleared and a new guest task would vanish). A real active tab
    // keeps the baseline global behavior (connect with its own connector).
    const activeIsGuest = activeUserId() === VISITOR_ID;
    // PER-TAB authenticated (coherent with `useSession().isAuthenticated()`): a
    // REAL active account enables the connect with ITS OWN connector — even when
    // the GLOBAL session machine is not primed for this tab (post-login/switch
    // minimal-view, or the account being open only in another tab). The previous
    // `Boolean(currentSession()?.user)` gated on the global session, so a real
    // per-tab account whose global machine wasn't primed never connected → the
    // pill stuck on "unauthenticated" with no sync. The guest case stays
    // local-only: `activeIsGuest` forces `authenticated=false` (its connector
    // also refuses to fetch a token), and sign-out drops the account from the
    // registry so `activeUserId()` falls back to the visitor before we navigate.
    const authenticated = activeAuthenticated();
    const visitor = activeIsGuest || isVisitor();
    const priming = isPriming();

    // Silently skip auto-connect if we don't have a reason to be connected.
    // This prevents the "Visitor or no auth detected" warning during logout.
    if (!authenticated && !priming && !visitor) {
      return;
    }

    if (onTauri) {
      // The Tauri PowerSync SDK forbids `db.connect()` from JS — start the
      // sync loop in Rust instead. JS fetches the JWT here (cookies live
      // on the backend's origin, invisible to `document.cookie` from
      // `tauri.localhost`) and pushes it to Rust via the
      // `powersync_connect` / `powersync_update_token` commands.
      void handleTauriPowerSyncAutoConnect({
        database,
        apiClient: api,
        apiBase: SERVER_URL,
        powerSyncEndpoint: POWERSYNC_URL,
        isAuthenticated: authenticated,
        isVisitor: visitor,
        isPriming: priming,
        isConnecting: untrack(isConnecting),
        setIsConnecting,
      });
      return;
    }

    const connector = activeConnector();
    if (!connector) {
      return;
    }
    void handlePowerSyncAutoConnect({
      database,
      connector,
      isAuthenticated: authenticated,
      isVisitor: visitor,
      isPriming: priming,
      isConnecting: untrack(isConnecting),
      setIsConnecting,
    });
  });

  // ── Active-user → DB instance binding ──────────────────────────────────────
  // The active session's user id selects which per-user DB instance is exposed
  // by the context. When it changes (initial prime OR an account switch) we
  // load/create that user's instance and swap it in WITHOUT clearing anything.
  //
  // Multi-account boot determinism: while the session machine is still
  // `priming` (its `primeSession` actor reads localStorage asynchronously),
  // `currentSession()` is transiently `null`. Falling back to `VISITOR_ID`
  // here would (a) spin up a throwaway visitor DB on every boot of a real
  // account and (b) make `activeUserId` flip VISITOR→real once priming lands,
  // thrashing the per-user DB instances. Instead, during priming we read the
  // persisted active account synchronously so the FIRST (and only) DB created
  // for a remembered real account is that account's DB — no visitor detour,
  // no flip.
  const activeUserId = () => {
    // PRIMARY (reactive): the tab's URL `/u/<n>` index → account in the shared
    // registry. This is the per-tab source of truth and is reactive to in-app
    // navigation (switcher), so an in-app switch re-runs the DB-binding effect
    // and swaps the displayed data WITHOUT a reload. We read the registry
    // synchronously each time the pathname signal changes — a switch always
    // navigates, so re-reading on navigation is sufficient and avoids coupling
    // to an async store load.
    const fromUrl = resolvePerTabSyncUserId(
      readAccountsStateSync(),
      parseActiveIndexFromPath(routerPathname()),
    );
    if (fromUrl !== null) {
      // A guest account binds the VISITOR_ID DB; a real one binds its own.
      return fromUrl;
    }

    // FALLBACK (boot / no `/u/<n>` yet, e.g. an auth page or pre-redirect bare
    // path): keep the original session-driven resolution so a fresh boot still
    // binds the right DB before the redirect lands on `/u/<n>`.
    const sessionUserId = currentSession()?.user.id;
    if (sessionUserId) {
      return sessionUserId;
    }
    if (isPriming()) {
      const persisted = readActiveAccountSync();
      if (persisted?.kind === "real") {
        return persisted.userId;
      }
    }
    return VISITOR_ID;
  };

  // PER-TAB authenticated view, derived from the active account (mirrors
  // `useSession().isAuthenticated()`): true iff the active account is a REAL
  // (non-guest) account. `activeUserId()` returns `VISITOR_ID` for a guest tab,
  // a bare boot path with no resolvable real account, or post-sign-out (the
  // account is dropped from the registry before navigation). Used by BOTH the
  // connectivity bridge (`onAuthChanged`) and the auto-connect effect so they can
  // never disagree about whether this tab should sync.
  const activeAuthenticated = () => activeUserId() !== VISITOR_ID;

  let loadToken = 0;
  // Bounded auto-retry for a TRANSIENT DB (re)build failure (the slot is already
  // released on rejection by `getOrCreateDbForUser`). Without this, a one-off
  // `init()` failure — e.g. the shared sync worker briefly torn down by a sibling
  // DB's `disconnectAndClear()` during a real-account sign-out, racing the
  // guest-DB rebuild — would leave `db()` null forever (the binding effect won't
  // re-run on its own, since `activeUserId()` did not change), hanging the boot
  // shell after switching back to the guest.
  const MAX_DB_LOAD_RETRIES = 3;
  const DB_LOAD_RETRY_MS = 250;
  const loadDbForUser = (userId: string, attempt = 0) => {
    loadToken += 1;
    const token = loadToken;
    setIsLoading(true);
    void (async () => {
      try {
        const instance = await getOrCreateDbForUser(userId === VISITOR_ID ? null : userId);
        if (token !== loadToken) {
          return; // a newer switch superseded this load
        }
        // CC-29: bind the connector + DB ATOMICALLY. These are two separate
        // signals; written one after another (this runs in an async `.then`,
        // outside any Solid batch) the auto-connect effect fires after the
        // FIRST write and observes a TORN pair — the new account's connector
        // paired with the OLD, about-to-be-retired guest DB. On a fresh
        // registration that mixed run connected the GUEST DB with the real
        // account's connector (cross-account contamination) and set the shared
        // `isConnecting` flag true; when the effect re-ran with the coherent
        // (real DB + real connector) pair, `isConnecting` was still true (and
        // read via `untrack`, so its later reset never re-triggered the effect)
        // → the real account's DB never connected and sync-DOWN stayed dead
        // until a full reload. `batch` guarantees observers only ever see the
        // fully-swapped, coherent state, so the effect connects the RIGHT DB
        // with the RIGHT connector on its first (and only) run.
        batch(() => {
          setActiveConnector(instance.connector);
          setDb(instance.db);
          setSyncError(null);
          setIsLoading(false);
        });
        // CC-42: this account just became the tab's ACTIVE identity. Any CRUD
        // it queued while another account was active was DEFERRED by the
        // connector's upload gate (see `createWebConnector`), and if its sync
        // stream stayed connected across the switch there is no reconnect to
        // flush it — so nudge the upload queue explicitly now.
        try {
          instance.db.syncStreamImplementation?.triggerCrudUpload();
        } catch {
          // Not connected yet — the upcoming (re)connect flushes the queue.
        }
      } catch (caught) {
        if (token !== loadToken) {
          return;
        }
        const err = caught instanceof Error ? caught : new Error(String(caught));
        console.error("[PowerSyncProvider] init error:", err);
        if (attempt < MAX_DB_LOAD_RETRIES) {
          // Re-attempt the SAME user; the poisoned promise slot was cleared on
          // rejection, so this rebuilds from scratch and self-heals.
          setTimeout(() => {
            if (token === loadToken) {
              loadDbForUser(userId, attempt + 1);
            }
          }, DB_LOAD_RETRY_MS);
          return;
        }
        setSyncError(err);
        setIsLoading(false);
      }
    })();
  };

  // React to active-user changes (drives both initial boot and account switch).
  let lastUserKey: string | null = null;
  createEffect(() => {
    const uid = activeUserId();
    const key = userKey(uid);
    if (key === lastUserKey) {
      return;
    }
    lastUserKey = key;
    loadDbForUser(uid);
  });

  const disconnectAndClear = async () => {
    const database = db();
    const connector = activeConnector();
    if (!database) {
      return;
    }
    const start = Date.now();
    console.info("[PowerSyncProvider] Starting database cleanup...");
    try {
      // 1. Explicitly pause sync first to avoid contention during cleanup.
      //    On Tauri, the sync runs in Rust — disconnect there too.
      await (onTauri ? tauriDisconnect(database) : database.disconnect());

      // 2. Clear local token cache
      await connector?.clearTokenCache();

      // 3. Wipe the database
      await database.disconnectAndClear();

      // 4. Forget this user's instance so a future re-login rebuilds it fresh.
      const key = lastUserKey ?? VISITOR_ID;
      dbInstances.delete(key);
      dbInstancePromises.delete(key);

      // 5. Cancel OS-scheduled reminders that belonged to the wiped data, so
      //    they don't fire for now-deleted todos (e.g. visitor → sign-in).
      await clearAllScheduledReminders();
      console.info(`[PowerSyncProvider] Database cleanup completed in ${Date.now() - start}ms`);
    } catch (err) {
      console.error("[PowerSyncProvider] Failed to clear database:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const value: PowerSyncContextValue = {
    db,
    isLoading,
    error: syncError,
    disconnectAndClear,
    destroyAccountData: async (userId: string) => destroyDbForUser(userId),
    flushGuestUpgrade: async (userId: string) => flushGuestDataToServer(userId),
    connectivityRef,
  };

  return <PowerSyncContext.Provider value={value}>{props.children}</PowerSyncContext.Provider>;
};

/**
 * Hook (factory de signals) para acceder al PowerSyncContext.
 * Lanza si se usa fuera del provider.
 */
export function usePowerSyncContext(): PowerSyncContextValue {
  const ctx = useContext(PowerSyncContext);
  if (!ctx) {
    throw new Error("[usePowerSyncContext] used outside <PowerSyncProvider>");
  }
  return ctx;
}
